// src/pages/CarBrandModelsPage.tsx - REFACTORED
import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from '../components/Router';
import { Header } from '../components/Header';
import { carPricesApi, CarModelWithBrand, CarBrand } from '../lib/carPricesApi';
import { LayoutWithBanners } from '../components/LayoutWithBanners';
// Import the fixed slugify function
import { slugify } from '../utils/slugify';

export function CarBrandModelsPage() {
  const { brandSlug } = useParams<{ brandSlug: string }>();
  const [models, setModels] = useState<CarModelWithBrand[]>([]);
  const [brand, setBrand] = useState<CarBrand | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [selectedBodyType, setSelectedBodyType] = useState<string>('');
  const [selectedTransmission, setSelectedTransmission] = useState<string>('');
  const [selectedSeats, setSelectedSeats] = useState<string>('');

  useEffect(() => {
    if (brandSlug) {
      loadBrandAndModels();
    } else {
      setError("Brand ID not found in URL.");
      setLoading(false);
    }
  }, [brandSlug]);

  const loadBrandAndModels = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [brandData, modelsData] = await Promise.all([
        carPricesApi.getBrand(brandSlug),
        carPricesApi.getModels(brandSlug)
      ]);
      
      if (!brandData) {
        throw new Error('Brand not found');
      }
      
      setBrand(brandData);
      setModels(modelsData);
    } catch (error) {
      console.error('Error loading data:', error);
      setError(`Failed to load data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number | null) => {
    if (!price) return 'Prix sur demande';
    return `${price.toLocaleString()} TND`;
  };

  // Get unique values for filter options
  const bodyTypes = useMemo(() => {
    const types = [...new Set(models.map(model => model.carrosserie).filter(Boolean))];
    return types.sort();
  }, [models]);

  const transmissions = useMemo(() => {
    const types = [...new Set(models.map(model => model.transmission).filter(Boolean))];
    return types.sort();
  }, [models]);

  const seatOptions = useMemo(() => {
    const seats = [...new Set(models.map(model => model.nombre_de_places).filter(Boolean))];
    return seats.sort((a, b) => Number(a) - Number(b));
  }, [models]);

  // Filter and sort models
  const filteredAndSortedModels = useMemo(() => {
    let filtered = [...models];
    
    if (selectedBodyType) {
      filtered = filtered.filter(model => model.carrosserie === selectedBodyType);
    }
    
    if (selectedTransmission) {
      filtered = filtered.filter(model => model.transmission === selectedTransmission);
    }
    
    if (selectedSeats) {
      filtered = filtered.filter(model => model.nombre_de_places === selectedSeats);
    }
    
    filtered.sort((a, b) => {
      if (a.starting_price === null && b.starting_price === null) return 0;
      if (a.starting_price === null) return 1;
      if (b.starting_price === null) return -1;
      return a.starting_price - b.starting_price;
    });
    
    return filtered;
  }, [models, selectedBodyType, selectedTransmission, selectedSeats]);

  const clearFilters = () => {
    setSelectedBodyType('');
    setSelectedTransmission('');
    setSelectedSeats('');
  };

  return (
    <div className="min-h-screen bg-white">
      <style jsx global>{`
        body {
          background-color: white !important;
        }
      `}</style>
      <Header />
      
      {/* Header Section - Clean & Minimal */}
      <div className="bg-white -mt-16 relative z-10 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center">
            <Link to="/neuf" className="text-[#D5462E] hover:text-[#b83a24] mr-2 text-sm flex items-center font-medium">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Retour
            </Link>
            <div className="h-4 w-px bg-gray-300 mx-2"></div>
            <h1 className="text-xl font-semibold text-gray-900">
              {brand?.name || 'Modèles'}
            </h1>
          </div>
        </div>
      </div>

      <LayoutWithBanners 
        pageLocation="car-prices"
        showTopBanner={true}
        showBottomBanner={true}
        showLeftSidebar={false}
        showRightSidebar={false}
        className="bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-white">
          {/* Filters Section - Filled Style Inputs */}
          <div className="bg-white rounded-3xl p-6 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <h2 className="text-sm font-semibold text-gray-700">Filtres:</h2>
              
              {/* Body Type Filter */}
              <div className="flex items-center">
                <label htmlFor="bodyType" className="text-sm text-gray-600 mr-2 font-medium">Carrosserie:</label>
                <select
                  id="bodyType"
                  value={selectedBodyType}
                  onChange={(e) => setSelectedBodyType(e.target.value)}
                  className="text-sm bg-gray-100 border-0 rounded-2xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#D5462E]/10 focus:bg-white transition-all font-medium"
                >
                  <option value="">Toutes</option>
                  {bodyTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              
              {/* Transmission Filter */}
              <div className="flex items-center">
                <label htmlFor="transmission" className="text-sm text-gray-600 mr-2 font-medium">Transmission:</label>
                <select
                  id="transmission"
                  value={selectedTransmission}
                  onChange={(e) => setSelectedTransmission(e.target.value)}
                  className="text-sm bg-gray-100 border-0 rounded-2xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#D5462E]/10 focus:bg-white transition-all font-medium"
                >
                  <option value="">Toutes</option>
                  {transmissions.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              
              {/* Seats Filter */}
              <div className="flex items-center">
                <label htmlFor="seats" className="text-sm text-gray-600 mr-2 font-medium">Places:</label>
                <select
                  id="seats"
                  value={selectedSeats}
                  onChange={(e) => setSelectedSeats(e.target.value)}
                  className="text-sm bg-gray-100 border-0 rounded-2xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#D5462E]/10 focus:bg-white transition-all font-medium"
                >
                  <option value="">Toutes</option>
                  {seatOptions.map(seats => (
                    <option key={seats} value={seats}>{seats} places</option>
                  ))}
                </select>
              </div>
              
              {/* Clear Filters Button */}
              <button
                onClick={clearFilters}
                className="text-sm text-[#D5462E] hover:text-[#b83a24] font-semibold"
              >
                Effacer les filtres
              </button>
              
              {/* Results Count */}
              <div className="ml-auto text-sm text-gray-600 font-medium">
                {filteredAndSortedModels.length} {filteredAndSortedModels.length === 1 ? 'modèle' : 'modèles'} trouvé{filteredAndSortedModels.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="bg-white/80 backdrop-blur-xl rounded-3xl overflow-hidden border border-gray-200 animate-pulse">
                  <div className="h-40 bg-gray-200"></div>
                  <div className="p-4">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-5 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-50 rounded-full mb-4 border border-red-100">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-red-500 mb-4 text-sm font-medium">{error}</p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={loadBrandAndModels}
                  className="px-5 py-2.5 bg-[#D5462E] text-white rounded-full hover:bg-[#b83a24] transition-colors text-sm flex items-center font-semibold"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Réessayer
                </button>
                <Link
                  to="/neuf"
                  className="px-5 py-2.5 border border-gray-200 text-gray-700 rounded-full hover:bg-gray-50 transition-colors text-sm flex items-center font-semibold"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Retour aux marques
                </Link>
              </div>
            </div>
          ) : filteredAndSortedModels.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4 border border-gray-200">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <p className="text-gray-500 text-sm mb-4 font-medium">
                {models.length === 0 
                  ? "Aucun modèle disponible pour cette marque." 
                  : "Aucun modèle ne correspond à vos filtres."}
              </p>
              {models.length > 0 && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center px-5 py-2.5 bg-[#D5462E] text-white rounded-full hover:bg-[#b83a24] transition-colors text-sm font-semibold"
                >
                  Effacer les filtres
                </button>
              )}
              {models.length === 0 && (
                <Link
                  to="/neuf"
                  className="inline-flex items-center px-5 py-2.5 bg-[#D5462E] text-white rounded-full hover:bg-[#b83a24] transition-colors text-sm font-semibold"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Retour aux marques
                </Link>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredAndSortedModels.map((model) => (
                <Link
                  key={model.id}
                  // UPDATED LINK GENERATION: Use slugify(model.name) to force the correct URL format
                  to={`/neuf/${brand?.slug || brand?.id}/${slugify(model.name)}`}
                  className="bg-white/80 backdrop-blur-xl rounded-3xl overflow-hidden border border-gray-200 hover:border-[#D5462E]/30 transition-all"
                >
                  <div className="h-40 bg-white relative overflow-hidden">
                    {model.images && model.images.length > 0 ? (
                      <img
                        src={model.images[0]}
                        alt={model.name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-medium text-gray-900">{model.name}</h3>
                    <p className="text-xs text-gray-500 mt-1 font-medium">
                      {model.engine && <span>{model.engine}</span>}
                      {model.transmission && <span> • {model.transmission}</span>}
                    </p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex flex-wrap gap-1">
                        {model.carrosserie && (
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-xl font-medium border border-gray-200">
                            {model.carrosserie}
                          </span>
                        )}
                        {model.nombre_de_places && (
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-xl font-medium border border-gray-200">
                            {model.nombre_de_places} places
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-base font-semibold text-[#D5462E]">
                        {formatPrice(model.starting_price)}
                      </p>
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </LayoutWithBanners>
    </div>
  );
}